import { DynamicValue } from "@faedev/dynamic-values";
import { composeClassNames } from "@faedev/editor-utils";
import { useRouter } from "next/router";
import { ReactElement, useContext, useEffect, useState } from "react";
import { Alert, Button, Modal } from "react-bootstrap";
import { useCookies } from "react-cookie";
import Preloader from "src/components/Preloader";
import SubscriptionCard from "src/components/SubscriptionCard";
import Translate from "src/components/Translate";
import constants from "src/constants";
import { UserContext } from "src/contexts/UserContext";
import getCookieByName from "src/functions/getCookieByName";
import gutter from "src/functions/gutter";
import showPriceInSubscriptionCard from "src/functions/showPriceInSubscriptionCard";
import translate from "src/functions/translate";
import validateAuthToken from "src/functions/validateAuthToken";
import Small_cancel from "src/icons/Small-cancel";
import Small_chevron_right from "src/icons/Small-chevron-right";
import { sAlert } from "src/styles/sAlert.module.scss";
import { sButton } from "src/styles/sButton.module.scss";
import { sButtonClose } from "src/styles/sButtonClose.module.scss";
import { sModal } from "src/styles/sModal.module.scss";
import { sModalSubscription } from "src/styles/sModalSubscription.module.scss";

type Props = {
  className?: (string | string) | null | undefined;
  alert?: ReactElement | null | undefined;
  openModalSubscription?: DynamicValue<boolean>;
};

function ModalSubscription({ className, openModalSubscription }: Props) {
  const { locale: lang, push } = useRouter();
  const [data, setData] = useState(null);
  const [active, setActive] = useState<number>();
  const [failed, setFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loadingBuySubscription, setLoadingBuySubscription] = useState(false);
  const { loggedUser } = useContext(UserContext);
  const [cookies, setCookie] = useCookies(null);

  useEffect(() => {
    fetch(`${constants.Paths.apiUrl}/subscriptions?_sort=period:desc`, {
      method: "GET",
    }).then(async (res) => {
      if (res.ok) {
        const result = await res.json();
        setData(result);
        setActive(result[1].id);
      }
    });
  }, []);

  const handleSubmit = () => {
    const cookieOrderId = getCookieByName(`orderId`);
    fetch(`${constants.Paths.apiUrl}/orders`, {
      method: "POST",
      headers: loggedUser
        ? validateAuthToken(getCookieByName(`auth-token`), {
            "Content-Type": "application/json",
          })
        : { "Content-Type": "application/json" },
      body: JSON.stringify({
        order_id: cookieOrderId ? cookieOrderId : "",
        item: {
          entityId: active,
          entityType: "subscription",
        },
      }),
    }).then(async (res) => {
      if (res.ok) {
        setCookie(`orderId`, await res.text(), constants.CookiesOptions);
        push(constants.Paths.pageCart);
      } else {
        setLoadingBuySubscription(false);
        let status = await res.json();
        status.message === "already in"
          ? push(constants.Paths.pageCart)
          : () => {
              setFailed(true);
              setErrorMessage(`Could not add to cart, please try again.`);
            };
      }
    });
  };

  const subscriptions = constants.Finance.subscriptionPrices;
  const sortedSubscriptions = subscriptions.map(
    (subscriptionPosition) =>
      data &&
      Array.isArray(data) &&
      data.find(
        (subscription) => Number(subscription.id) === subscriptionPosition
      )
  );

  return (
    <Modal
      show={openModalSubscription.value}
      className={composeClassNames([sModal, sModalSubscription, className])}
      onHide={() => openModalSubscription.change(false)}
    >
      <Modal.Header>
        <Modal.Title>
          <Translate tKey={`Choose a subscription`} />
        </Modal.Title>
        <Button
          variant="link"
          onClick={function () {
            return openModalSubscription.change(false);
          }}
          size="lg"
          className={composeClassNames([sButtonClose, `btn-close`])}
        >
          <Small_cancel />
        </Button>
      </Modal.Header>
      <Modal.Body>
        {data &&
          sortedSubscriptions.map((sub) => (
            <SubscriptionCard
              activeId={active}
              description={sub.description}
              id={sub.id}
              key={`sub-${sub.id}`}
              price={
                sub.id === 1
                  ? showPriceInSubscriptionCard(
                      sub.price,
                      sub.currency,
                      "you get 5 months free"
                    )
                  : showPriceInSubscriptionCard(
                      sub.price,
                      sub.currency,
                      "1 month membership for"
                    )
              }
              setActive={setActive}
              title={translate(sub.title + " subscription", lang)}
              recommended={1}
            />
          ))}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant={`warning`}
          size="lg"
          className={composeClassNames([
            sButton,
            "btn-icon-after",
            "btn-shine",
          ])}
          onClick={handleSubmit}
        >
          {loadingBuySubscription ? (
            <Preloader size="sm" />
          ) : (
            <>
              <Translate
                tKey={constants.Marketing.subscriptionMembersBtnTitle}
              />
              <Small_chevron_right />
            </>
          )}
        </Button>
        <Button
          variant="link"
          size="sm"
          className={composeClassNames([sButton])}
          onClick={() => push("/prices")}
        >
          <Translate tKey={`Other pricing options`} />
        </Button>
        {failed && (
          <Alert
            variant="danger"
            className={sAlert}
            style={gutter(`margin`, `vertical`, `md`)}
          >
            <Translate tKey={errorMessage} />
          </Alert>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default ModalSubscription;
